import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import InputMask from "react-input-mask";
import {
  IAvailabilities,
  IResourceType,
} from "../../../../@types/resource.type";
import {
  addOrUpdateResource,
  deleteResource,
  getAllResources,
} from "../../../../services/api/resource/resourceService";
import { storageUserGet } from "../../../../storage/storageUser";

import { IKeyValue } from "../../../../@types/key-value.type";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { ProjectList } from "../ProjectList";
import { useConfirm } from "../../../../hooks/useConfirm";
import { getAllAreasIdsAndNames } from "../../../../services/api/areas/AreasService";
import { IResourceCostType } from "../../../../@types/resource-cost.type";
import { getAllCostCentersIdsAndNames } from "../../../../services/api/costCenter/costCenterService";
import { getAllPositionsIdsAndNames } from "../../../../services/api/positions/PositionsService";
import usePermission from "../../../../hooks/usePermission";
import { FeatureStatic } from "../../../../@types/statics/feature.static";
import { Availability } from "../Availability";
import { getDefaultAvailabilityById } from "../../../../services/api/defaultAvailability/defaultAvailability";
import { ModalAvailability } from "../ModalAvailability";
import { getAllResourceCustomField } from "../../../../services/api/resourceCustomField/resourceCustomFieldService";
import { ICustomField } from "../../../../@types/customField.type";
import { mapFieldTypeOptionsValue } from "../../../../utils/mapFieldTypeOptions";
import { validateCustomFields } from "../../../../utils/validateCustomFields";
import { OptionsDynimicFields } from "../../../../components/OptionsDynimicFields";
import { includeCustomFieldstoApi } from "../../adapters/resourceCustomFields";
import styles from "./styles.module.scss";
import { CostHistoryForm } from "../CostForm";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { hasFutureAllocation } from "../../../../services/api/Allocations/AllocationsService";
import { searchConfigurationsByKeys } from "../../../../services/api/Configurations/ConfigurationService";
import { IApiResponse } from "../../../../@types/api-response.type";
import { IConfiguration } from "../../../../@types/configuration.type";

const resourceSchema = z.object({
  firstname: z.string().min(1, "Primeiro Nome é obrigatório"),
  lastname: z.string().min(1, "Sobrenome é obrigatório"),
  email: z
    .string()
    .optional()
    .refine((email) => !email || /\S+@\S+\.\S+/.test(email), {
      message: "Formato do e-mail inválido",
    }),
  phone: z.string().optional(),
  areaId: z.string(),
  managerId: z.string().optional(),
  costCenterId: z.string().optional(),
  positionId: z.string(),
  admissionDate: z.string().min(1, "Data de Admissão é obrigatória"),
  isAllocatable: z.boolean().optional(),
  isDefaultAvailability: z.boolean().optional(),
  dismissed: z.boolean().optional(),
  dismissedDate: z.string().nullable().optional(),
  allocatableDate: z.string().nullable().optional(),
});

export type ResourceData = z.infer<typeof resourceSchema>;

interface FormResourceProps {
  pageType: "create" | "update";
  resourceData?: IResourceType;
  setOpenModalAvailability: React.Dispatch<React.SetStateAction<boolean>>;
  isSyncActive?: boolean;
}
interface IComboBoxOption {
  key: string;
  text: string;
}

export const FormResource: React.FC<FormResourceProps> = ({
  pageType,
  resourceData,
  setOpenModalAvailability,
  isSyncActive = false,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [isEnabled, setIsEnabled] = useState(true);
  const [syncSystemName, setSyncSystemName] = useState<string | null>(null);
  const [resourceCost, setResourceCost] = useState<IResourceCostType | null>(null);
  const [formDataCustomFields, setFormDataCustomFields] = useState({} as Record<string, any>);
  const [errorsFormDataCustomFields, setErrorsFormDataCustomFields] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const {
    handleSubmit,
    reset,
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ResourceData>({
    resolver: zodResolver(resourceSchema),
    defaultValues: {
      firstname: resourceData?.firstname || "",
      lastname: resourceData?.lastname || "",
      email: resourceData?.email || "",
      phone: resourceData?.phone || "",
      areaId: resourceData?.areaId || "",
      managerId: resourceData?.managerId || "",
      costCenterId: resourceData?.costCenterId || "",
      positionId: resourceData?.positionId || "",
      admissionDate: resourceData?.admissionDate || "",
      isAllocatable: resourceData?.isAllocatable || false,
      isDefaultAvailability: resourceData?.isDefaultAvailability,
      dismissed: resourceData?.dismissed,
      dismissedDate: resourceData?.dismissedDate
    },
  });
  const [optionsManager, setOptionsManager] = useState<IComboBoxOption[]>([]);
  const [optionsArea, setOptionsArea] = useState<IComboBoxOption[]>([]);
  const [optionsCostCenter, setOptionsCostCenter] = useState<IComboBoxOption[]>([]);
  const [resourceCustomField, setResourceCustomField] = useState<ICustomField[]>([]);
  const [optionsPosition, setOptionsPosition] = useState<IComboBoxOption[]>([]);

  const navigate = useNavigate();
  const permissions = usePermission(FeatureStatic.resources.id);

  const isAllocatable = watch("isAllocatable");
  const isDefaultAvailability = watch("isDefaultAvailability");
  const dismissed = watch("dismissed");

  const [availabilities, setAvailabilities] = useState<IAvailabilities[]>(
    resourceData?.availabilities ?? []
  );
  const [weekHours, setWeekHours] = useState<number>(
    resourceData?.weekHours ?? 0
  );

  const handleOpen = () => {
    setOpenModal(true);
  };
  const handleClose = () => {
    setOpenModal(false);
  };

  const [openFutureAllocationsConfirm, setOpenFutureAllocationsConfirm] = useState(false);

  const handleCloseFutureAllocationsConfirm = () => {
    setOpenFutureAllocationsConfirm(false);
  };

  const {
    showConfirm: showFutureAllocationsConfirm,
    Confirm: FutureAllocationsConfirmModal,
  } = useConfirm(
    "Atenção",
    openFutureAllocationsConfirm,
    handleCloseFutureAllocationsConfirm,
    "Esse colaborador possui alocações em andamento ou futuras. Deseja realmente colocar esse colaborador como não alocavél? Se 'Sim' todas as alocações a partir dessa data serão removidas.",
    true
  );

  useEffect(() => {
    if (
      resourceCustomField?.length > 0 &&
      resourceData?.customFieldsObject !== undefined
    ) {
      const customFieldsInitialState = resourceCustomField.reduce(
        (acc, field) => {
          let initialValue;
          switch (mapFieldTypeOptionsValue[field.customFieldTypeId]) {
            case "booleano":
              initialValue = "Não";
              break;
            case "data":
              initialValue = "";
              break;
            case "lista":
              initialValue = "";
              break;
            case "textoMultlinhas":
            case "texto":
            default:
              initialValue = "";
          }
          acc[field.name] = resourceData.customFieldsObject?.[field.name]
            ? resourceData?.customFieldsObject[field.name]
            : initialValue;
          return acc;
        },
        {} as Record<string, any>
      );

      setFormDataCustomFields(() => ({
        ...customFieldsInitialState,
      }));
    }
  }, [resourceCustomField, resourceData]);

  useEffect(() => {
    const fetchAvailabilities = async () => {
      try {
        const response = await getDefaultAvailabilityById(
          "14174dbf-e805-4532-9b1c-35d7b3cead8f"
        );
        setAvailabilities(response.availabilities);
        setWeekHours(response.weekHours);
      } catch (error) {
        console.error("Erro ao buscar disponibilidade padrão:", error);
      }
    };

    if (
      !resourceData ||
      availabilities.length === 0 ||
      isDefaultAvailability === true
    ) {
      fetchAvailabilities();
    }
  }, [isDefaultAvailability]);

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
    }
    const loadOptions = async () => {
      try {
        const responseResourceCustomField = await getAllResourceCustomField();
        setResourceCustomField(responseResourceCustomField.result);

        const [
          responseManager,
          responseArea,
          responseCostCenter,
          responsePosition,
        ] = await Promise.allSettled([
          getAllResources(undefined, false),
          getAllAreasIdsAndNames(),
          getAllCostCentersIdsAndNames(),
          getAllPositionsIdsAndNames(),
        ]);
        const handleFulfilledResponse = (
          response: PromiseSettledResult<any>
        ) => {
          if (response.status === "fulfilled") {
            return response.value.map((item: IKeyValue) => ({
              key: item.key,
              text: item.value,
            }));
          }
          return [];
        };

        const resourceOptionsManager = handleFulfilledResponse(responseManager);
        setOptionsManager(resourceOptionsManager);

        const resourceOptionsArea = handleFulfilledResponse(responseArea);
        setOptionsArea(resourceOptionsArea);

        const resourceOptionsCostCenter =
          handleFulfilledResponse(responseCostCenter);
        setOptionsCostCenter(resourceOptionsCostCenter);

        const resourceOptionsPosition =
          handleFulfilledResponse(responsePosition);
        setOptionsPosition(resourceOptionsPosition);
      } catch (error) {
        console.error('Erro ao carregar opções:', error);
      }
    };
    loadOptions();
  }, []);

  const isSyncFieldDisabled = isSyncActive || (pageType === "update" && syncSystemName !== null);

  useEffect(() => {
    if (pageType === "update") {
      const checkSyncStatus = async () => {
        try {
          const response = await searchConfigurationsByKeys([
            "CONFIGURATION_CONVENIA_TOKEN",
            "CONFIGURATION_LG_USER",
            "CONFIGURATION_LG_PASSWORD",
            "CONFIGURATION_LG_URL",
            "CONFIGURATION_LG_ENVIRONMENT",
            "CONFIGURATION_LG_GUIDTENANT",
          ]);
          const configs = (response as IApiResponse<IConfiguration[]>).result;
          const isConveniaActive = configs.some(
            (x) => x.key === "CONFIGURATION_CONVENIA_TOKEN" && x.value !== ""
          );
          const isLGActive = configs.some(
            (x) =>
              (x.key === "CONFIGURATION_LG_USER" ||
                x.key === "CONFIGURATION_LG_PASSWORD" ||
                x.key === "CONFIGURATION_LG_URL" ||
                x.key === "CONFIGURATION_LG_ENVIRONMENT" ||
                x.key === "CONFIGURATION_LG_GUIDTENANT") &&
              x.value !== ""
          );
          if (isConveniaActive && isLGActive) {
            setSyncSystemName("LG e Convenia");
          } else if (isLGActive) {
            setSyncSystemName("LG");
          } else if (isConveniaActive) {
            setSyncSystemName("Convenia");
          }
        } catch (error) {
          console.error("Erro ao verificar sincronização:", error);
        }
      };
      checkSyncStatus();
    }
  }, [pageType]);

  const initialIsAllocatable = resourceData?.isAllocatable ?? false;

  const handleFormSubmit = async (data: ResourceData) => {
    const filteredAvailabilities = availabilities.map((availability) => {
      const { ...filteredAvailability } = availability;
      return filteredAvailability;
    });

    const resourceToApi = {
      id: resourceData?.id,
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email || null,
      phone: data.phone || null,
      areaId: data.areaId || null,
      managerId: data.managerId || null,
      costCenterId: data.costCenterId || null,
      positionId: data.positionId || null,
      resourceCost: resourceCost,
      admissionDate: data.admissionDate ? dayjs(data.admissionDate).startOf('day').add(12, 'hour').toISOString() : null,
      isAllocatable: data.isAllocatable,
      isDefaultAvailability: data.isDefaultAvailability,
      dismissed: data.dismissed,
      dismissedDate: data.dismissed && data.dismissedDate ? dayjs(data.dismissedDate).startOf('day').add(12, 'hour').toISOString() : null,
      availabilities: data.isAllocatable ? filteredAvailabilities || [] : [],
      allocatableDate: data.allocatableDate ? dayjs(data.allocatableDate).startOf('day').add(12, 'hour').toISOString() : null,
    };
    
    setErrorsFormDataCustomFields({});
    const newErrors: any = {};
    resourceCustomField.forEach((field) => {
      const error = validateCustomFields(
        field,
        formDataCustomFields[field.name]
      );

      if (error) {
        newErrors[field?.name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrorsFormDataCustomFields(newErrors);
      return;
    }
    if (pageType === "update" && initialIsAllocatable && !data.isAllocatable) {
      try {
        const hasFuture = await hasFutureAllocation(resourceData?.id!);
        if (hasFuture.result == "true") {

          const confirm = await showFutureAllocationsConfirm();
          if (!confirm) return; // usuário cancelou
        }
      } catch (err) {
        console.error("Erro ao verificar alocações futuras:", err);
        toast.error("Não foi possível verificar alocações futuras. Tente novamente.");
        return;
      }
    }

    const resourceWithCustomFields = includeCustomFieldstoApi(
      resourceToApi,
      formDataCustomFields
    );

    const user = storageUserGet();
    const author = `${user.given_name || ''} ${user.family_name || ''}`.trim() || 'Sistema';
    const resourceWithAuthor = {
      ...resourceWithCustomFields,
      author,
    };
    try {
      setLoading(true);
      const response = await addOrUpdateResource(resourceWithAuthor);
      if (response.statusCode === 200) {
        setOpenModalAvailability(openModal);
        toast.success("Colaborador salvo com sucesso!");
        navigate("/resources");
      } else {
        toast.error(
          `Não foi possível salvar o colaborador! Tente novamente mais tarde.`
        );
      }
    } catch (error: any) {
      console.error('Erro ao salvar colaborador:', error);

      const errorMessage = 'Erro ao salvar o colaborador. Tente novamente.';

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);
  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o colaborador?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const handleDelete = async () => {
    handleOpenModalConfirm();
    if (!resourceData?.id) return;

    const isConfirm = await showConfirm();

    if (isConfirm) {
      try {
        const response = await deleteResource(resourceData?.id);
        if (response.statusCode === 200) {
          toast.success("Colaborador excluído com sucesso!");
          navigate("/resources");
        } else {
          toast.error(
            "Não foi possível excluir o colaborador. Tente novamente."
          );
        }
      } catch (error) {
        toast.error("Erro ao excluir colaborador. Tente novamente.");
        console.log(error)
      }
    }
  };

  return (
    <>
      {FutureAllocationsConfirmModal}
      <form
        className={styles.formResource}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <h3 className={styles.title}>Informações Gerais</h3>

        {isSyncFieldDisabled && (
          <Alert severity="info" sx={{ marginBottom: "1rem" }}>
            Alguns campos estão desabilitados pois este colaborador está vinculado à sincronização com <strong>{syncSystemName}</strong>. Para alterá-los, atualize os dados diretamente no sistema de origem.
          </Alert>
        )}

        <div className={styles.fields}>
          <Controller
            name="firstname"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="Primeiro Nome"
                placeholder={
                  pageType === "create" ? "Insira o primeiro nome aqui..." : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                disabled={isSyncFieldDisabled}
                error={!!errors.firstname}
                helperText={errors.firstname?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="lastname"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="Sobrenome"
                placeholder={
                  pageType === "create" ? "Insira o sobrenome aqui..." : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                disabled={isSyncFieldDisabled}
                error={!!errors.lastname}
                helperText={errors.lastname?.message}
                fullWidth
              />
            )}
          />
        </div>

        <div className={styles.fields}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="E-mail"
                placeholder={
                  pageType === "create" ? "Insira o e-mail aqui..." : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                disabled={isSyncFieldDisabled}
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <InputMask
                mask="(99) 99999-9999"
                value={field.value}
                onChange={field.onChange}
                disabled={isSyncFieldDisabled}
              >
                {(inputProps: any): React.ReactNode => (
                  <TextField
                    {...inputProps}
                    label="Telefone"
                    placeholder={
                      pageType === "create" ? "Insira o telefone aqui..." : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    fullWidth
                  />
                )}
              </InputMask>
            )}
          />
        </div>

        <div className={styles.fields}>
          <Controller
            name="areaId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                disabled={isSyncFieldDisabled}
                options={optionsArea}
                getOptionLabel={(option) => option.text || ""}
                value={
                  optionsArea.find((option) => option.key === field.value) ||
                  null
                }
                onChange={(_, newValue) =>
                  field.onChange(newValue ? newValue.key : "")
                }
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Área"
                    placeholder={
                      pageType === "create" ? "Selecione sua Área" : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.areaId}
                    helperText={errors.areaId?.message}
                    fullWidth
                  />
                )}
              />
            )}
          />
          <Controller
            name="managerId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                disabled={isSyncFieldDisabled}
                options={optionsManager}
                getOptionLabel={(option) => option.text || ""}
                value={
                  optionsManager.find((option) => option.key === field.value) ||
                  null
                }
                onChange={(_, newValue) =>
                  field.onChange(newValue ? newValue.key : "")
                }
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Gestor"
                    placeholder={
                      pageType === "create" ? "Selecione seu Gestor" : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.managerId}
                    helperText={errors.managerId?.message}
                    fullWidth
                  />
                )}
              />
            )}
          />
        </div>
        <div className={styles.fields}>
          <Controller
            name="costCenterId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                disabled={isSyncFieldDisabled}
                options={optionsCostCenter}
                getOptionLabel={(option) => option.text || ""}
                value={
                  optionsCostCenter.find(
                    (option) => option.key === field.value
                  ) || null
                }
                onChange={(_, newValue) =>
                  field.onChange(newValue ? newValue.key : "")
                }
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Centro de Custo"
                    placeholder={
                      pageType === "create" ? "Selecione o Centro de Custo" : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.costCenterId}
                    helperText={errors.costCenterId?.message}
                    fullWidth
                  />
                )}
              />
            )}
          />
          <Controller
            name="positionId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                disabled={isSyncFieldDisabled}
                options={optionsPosition}
                getOptionLabel={(option) => option.text || ""}
                value={
                  optionsPosition.find(
                    (option) => option.key === field.value
                  ) || null
                }
                onChange={(_, newValue) =>
                  field.onChange(newValue ? newValue.key : "")
                }
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Função"
                    placeholder={
                      pageType === "create" ? "Selecione a Função" : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.positionId}
                    helperText={errors.positionId?.message}
                    fullWidth
                  />
                )}
              />
            )}
          />
          <Controller
            name="admissionDate"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <DatePicker
                label="Data de Admissão"
                value={field.value ? dayjs(field.value) : null}
                disabled={pageType === "update"}
                onChange={(newValue) => 
                  field.onChange(newValue?.format("YYYY-MM-DD HH:mm:ss") || "")
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputLabelProps: { shrink: true },
                    placeholder: pageType === "create" ? "Selecione a data de admissão" : "",
                    error: !!errors.admissionDate,
                    helperText: errors.admissionDate?.message,
                  },
                }}
              />
            )}
          />
        </div>
        <OptionsDynimicFields
          dynamicFields={resourceCustomField}
          formDataCustomFields={formDataCustomFields}
          setFormDataCustomFields={setFormDataCustomFields}
          errorsFormDataCustomFields={errorsFormDataCustomFields}
        />

        <div className={styles.divAllocatableAndSkills}>
          <div>
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '.5rem' }}>
              <Controller
                name="isAllocatable"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ margin: '0' }}
                    control={
                      <Switch
                        checked={field.value || false}
                        onChange={(event) => {
                          field.onChange(event.target.checked);
                          if (event.target.checked && !getValues('allocatableDate')) {
                            const admissionDate = resourceData?.admissionDate
                              ? dayjs(resourceData.admissionDate).format("YYYY-MM-DD HH:mm:ss")
                              : dayjs().format("YYYY-MM-DD HH:mm:ss");
                            setValue('allocatableDate', admissionDate);
                          }
                        }}
                        disabled={dismissed}
                      />
                    }
                    label="Permitir Alocação"
                  />
                )}
              />

              {isAllocatable != initialIsAllocatable && (
                <Controller
                  name="allocatableDate"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DatePicker
                      sx={{ width: "200px" }}
                      label="Data de Permitir Alocação"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(newValue) => field.onChange(newValue?.format("YYYY-MM-DD HH:mm:ss"))}
                      minDate={ resourceData && resourceData.admissionDate ? dayjs(resourceData.admissionDate) : dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "outlined",
                          InputLabelProps: { shrink: true },
                          error: !!errors.dismissedDate,
                          helperText: errors.dismissedDate?.message,
                        },
                      }}
                    />
                  )}
                />
              )}

              {isAllocatable && (
                <Controller
                  name="isDefaultAvailability"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ margin: '0' }}
                      control={
                        <Switch
                          checked={field.value || false}
                          onChange={(event) => field.onChange(event.target.checked)}
                        />
                      }
                      label="Disponibilidade Padrão"
                    />
                  )}
                />
              )}

              <Controller
                name="dismissed"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ margin: '0' }}
                    control={
                      <Switch
                        checked={field.value || false}
                        onChange={(event) => field.onChange(event.target.checked)}
                        disabled={isSyncActive}
                      />
                    }
                    label="Desligar Colaborador"
                  />
                )}
              />
              {dismissed && (
                <Controller
                  name="dismissedDate"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DatePicker
                      sx={{ width: "200px" }}
                      key={field.name}
                      label="Data de Desligamento"
                      value={dayjs(field.value).endOf('day')}
                      onChange={(newValue) => field.onChange(newValue?.format("YYYY-MM-DD HH:mm:ss"))}
                      disabled={isSyncActive}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "outlined",
                          InputLabelProps: { shrink: true },
                          error: !!errors.dismissedDate,
                          helperText: errors.dismissedDate?.message,
                        },
                      }}
                    />
                  )}
                />
              )}
            </div>

          </div>
        </div>

        <div className={styles.availabilityAndProjectsContainer}>
          {isAllocatable && (
            <div className={styles.availabilitySection}>
              <Availability
                isClickable={isDefaultAvailability ? false : true}
                availabilities={availabilities}
                weekHours={weekHours}
                onOpenModal={handleOpen}
              />
            </div>
          )}

          <div className={`${styles.projectsSection} ${!isAllocatable ? styles.fullWidth : ''}`}>
            <h3 className={styles.title}>Projetos</h3>
            <ProjectList id={resourceData?.id} />
          </div>
        </div>

        <CostHistoryForm
          pageType={pageType}
          resource={resourceData}
          setExternalButtonsEnabled={setIsEnabled}
          setExternalSubmit={setResourceCost}
        />

        <div className={styles.btnContainer}>
          {pageType === "update" && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              disabled={!isEnabled || isSyncActive}
              onClick={handleDelete}
              className={`${styles.btnsDeleted} ${!isEnabled || isSyncActive ? styles.buttonDeleted : ""
                }`}
            >
              Excluir Colaborador
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={styles.btnsSave}
          >
            Salvar
          </Button>
        </div>
      </form>
      
      <ModalAvailability
        open={openModal}
        handleClose={handleClose}
        availabilities={availabilities}
        setAvailabilities={setAvailabilities}
        setWeekHours={setWeekHours}
      />
      {Confirm}
    </>
  );
};
